import { useState, useEffect } from 'react'
import { ScrollArea } from '@digital-twins/design-system/components/ui/scroll-area'
import { Sparkles, FileText } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@digital-twins/design-system/components/ui/tooltip'
import { TextClamp } from '../../text/TextClamp/TextClamp'
import {
  findAssetReportContextItem,
  formatAssetReportLabel,
  type AssetReportContextItem,
} from '../../charts/asset-report-label'
import { getQuestionValue } from '../../../lib/aggregation'
import {
  laneAssetIdsForQuestion,
  type MonadicLaneScopeFact,
} from '../../../lib/monadicLaneScope'

/**
 * The list's chrome, in the viewer's language. It is the viewer's interface,
 * not study content, so the host app supplies it from its own i18n.
 */
export interface TextResponsesListLabels {
  noThemesTitle: string
  noThemesMessage: string
  /** The view toggle's two options. */
  themes: string
  raw: string
  noResponses: string
  /** A citation tooltip's heading; `number` is the answer's 1-based position. */
  responseNumber: (number: number) => string
}

interface TextResponsesListProps {
  labels: TextResponsesListLabels
  question: any
  responses: any[]
  themeSummary?: TextSummary | null
  /** Maximum lines to show before truncating with "Show more" (default: 4) */
  maxLines?: number
  /** Line height for text content (default: 2) */
  lineHeight?: number | string
  /** Gap between response items in pixels (default: 12) */
  itemGap?: number
  /** Padding inside each response card in pixels (default: 12) */
  itemPadding?: number
  /** Padding around the list container in pixels (default: 16) */
  containerPadding?: number
  /**
   * Tested-asset titles. A `per_asset` question persists one answer per lane
   * as `{ [assetId]: text }`; when the keys resolve here the raw view groups
   * the answers under the asset title instead of flattening the lanes.
   */
  assetContextItems?: Array<AssetReportContextItem> | null
  /**
   * Study-level lane fact. When this question is named, grouping uses the
   * fact's asset ids rather than inferring from the answer's keys.
   */
  monadicLaneScope?: MonadicLaneScopeFact | null
}

/**
 * One printable answer, with the asset lane it was given in when known:
 * `groupKey` is the lane's asset id (the identity), `group` its title (display).
 */
interface ResponseEntry {
  text: string
  group: string | null
  groupKey?: string | null
}

interface ResponseGroup {
  /** The lane's asset id; null for ungrouped answers. */
  key: string | null
  label: string | null
  entries: Array<{ text: string; index: number }>
}

/**
 * Group entries by lane in first-seen order; ungrouped answers keep `label: null`.
 * Lanes are told apart by asset id, never by title: two assets sharing a title
 * are two groups. An entry with no id groups by title.
 */
export function groupResponseEntries(entries: Array<ResponseEntry>): Array<ResponseGroup> {
  const groups: Array<ResponseGroup> = []
  entries.forEach((entry, index) => {
    const key = entry.groupKey ?? entry.group
    let group = groups.find((candidate) => candidate.key === key)
    if (!group) {
      group = { key, label: entry.group, entries: [] }
      groups.push(group)
    }
    group.entries.push({ text: entry.text, index })
  })
  return groups
}

interface Theme {
  theme: string
  description: string
  citation_indices: number[]
  percent?: number
  quotes?: string[]
}

interface TextSummary {
  themes: Theme[]
}

export function TextResponsesList({
  labels,
  question,
  responses,
  themeSummary,
  maxLines = 4,
  lineHeight = 2,
  itemGap = 12,
  itemPadding = 12,
  containerPadding = 16,
  assetContextItems,
  monadicLaneScope,
}: TextResponsesListProps) {
  const summary = themeSummary ?? null
  const [viewMode, setViewMode] = useState<'summary' | 'raw'>(
    summary?.themes?.length ? 'summary' : 'raw',
  )

  const extractLeafStrings = (val: any): string[] => {
    if (val === undefined || val === null) return []
    if (typeof val === 'string') return [val]
    if (typeof val === 'number' || typeof val === 'boolean') return [String(val)]
    if (Array.isArray(val)) return val.flatMap(extractLeafStrings)
    if (typeof val === 'object') return Object.values(val).flatMap(extractLeafStrings)
    return []
  }

  /**
   * A lane-merged dict is grouped by the study-level fact's asset ids.
   * A `per_asset` dict whose every key is a tested asset still groups the
   * same way when the fact is absent (historical blobs). Anything else
   * (strings, arrays, text-matrix cells) is flattened exactly as before.
   */
  const collectEntries = (value: any): ResponseEntry[] => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const laneKeys = Object.keys(value)
      const factIds = laneAssetIdsForQuestion(question, monadicLaneScope)
      const isFactKeyed = factIds.length > 0
      const isAssetKeyed =
        !isFactKeyed &&
        laneKeys.length > 0 &&
        laneKeys.every((key) => findAssetReportContextItem([key], assetContextItems) !== null)
      if (isFactKeyed) {
        return factIds.flatMap((key) =>
          extractLeafStrings(value[key]).map((text) => ({
            text,
            group: formatAssetReportLabel(key, assetContextItems, [key]),
            groupKey: key,
          })),
        )
      }
      if (isAssetKeyed) {
        return laneKeys.flatMap((key) =>
          extractLeafStrings(value[key]).map((text) => ({
            text,
            group: formatAssetReportLabel(key, assetContextItems),
            groupKey: key,
          })),
        )
      }
    }
    return extractLeafStrings(value).map((text) => ({ text, group: null }))
  }

  // Filter valid responses and extract values through the shared resolver
  const validEntries = responses
    .flatMap((response) => collectEntries(getQuestionValue(response, question)))
    .filter((entry) => entry.text.trim().length > 0)
  const validResponses = validEntries.map((entry) => entry.text)
  const responseGroups = groupResponseEntries(validEntries)

  useEffect(() => {
    setViewMode(summary?.themes?.length ? 'summary' : 'raw')
  }, [summary])

  const renderSummary = () => {
    if (!summary) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
          <div className="bg-muted p-3 rounded-full mb-3">
            <Sparkles className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="font-medium mb-1">{labels.noThemesTitle}</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-xs">
            {labels.noThemesMessage}
          </p>
        </div>
      )
    }

    return (
      <div className="p-4 space-y-6">
        <div className="grid gap-4">
          {summary.themes.map((theme, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-start gap-2">
                <div
                  className="flex items-center justify-center rounded-full bg-brand-accent/10 text-foreground text-xs font-medium mt-0.5 min-w-6 h-6"
                >
                  {i + 1}
                </div>
                <div className="space-y-1 flex-1">
                  <h5 className="font-medium text-sm leading-tight">{theme.theme}</h5>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {theme.description}
                  </p>

                  {/* Citations */}
                  {theme.citation_indices && theme.citation_indices.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 pt-1">
                      {theme.citation_indices.map((idx, citationIndex) => (
                        <TooltipProvider key={`${i}-${citationIndex}`}>
                          <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded font-medium bg-muted hover:bg-brand-accent/10 hover:text-foreground cursor-help transition-colors border border-transparent hover:border-brand-accent/20 text-xs"
                              >
                                #{idx + 1}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="p-3 max-w-xs bg-popover text-popover-foreground border border-border shadow-md">
                              <div className="space-y-1">
                                <div
                                  className="uppercase tracking-wider text-muted-foreground font-semibold text-xs"
                                >
                                  {labels.responseNumber(idx + 1)}
                                </div>
                                <p className="text-xs italic text-popover-foreground/90 leading-relaxed">
                                  "{validResponses[idx]}"
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* View toggle - show for all users but with appropriate options */}
      <div className="flex items-center justify-end mb-4">
        {/* View Toggle */}
        <div className="flex items-center bg-background rounded-md border border-border/50 p-0.5 shadow-sm">
          <button
            onClick={() => setViewMode('summary')}
            className={`px-3 py-1 text-xs font-medium rounded-sm transition-all ${viewMode === 'summary'
              ? 'bg-brand-accent/10 text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
          >
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" />
              <span>{labels.themes}</span>
            </div>
          </button>
          <button
            onClick={() => setViewMode('raw')}
            className={`px-3 py-1 text-xs font-medium rounded-sm transition-all ${viewMode === 'raw'
              ? 'bg-brand-accent/10 text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
          >
            <div className="flex items-center gap-1.5">
              <FileText className="h-3 w-3" />
              <span>{labels.raw}</span>
            </div>
          </button>
        </div>
      </div>

      <ScrollArea className="w-full max-h-[600px]">
        {/* Word cloud view */}
        {viewMode === 'summary' && summary ? (
          renderSummary()
        ) : (
          <div
            className="flex flex-col"
            style={{ padding: containerPadding, gap: itemGap }}
          >
            {validResponses.length > 0 ? (
              responseGroups.map((group, groupIndex) => (
                <div
                  key={group.key ?? `ungrouped-${groupIndex}`}
                  className="flex flex-col"
                  style={{ gap: itemGap }}
                  data-testid={group.label ? 'text-responses-asset-group' : undefined}
                >
                  {group.label && (
                    <div
                      className="text-xs font-medium text-muted-foreground"
                      data-testid="text-responses-asset-group-label"
                    >
                      {group.label}
                    </div>
                  )}
                  {group.entries.map(({ text, index }) => (
                    <div
                      key={index}
                      className="group bg-background rounded-lg border border-border/50 text-sm leading-relaxed hover:bg-muted/30 transition-colors relative"
                      style={{ padding: itemPadding }}
                    >
                      <div
                        className="absolute right-3 opacity-0 group-hover:opacity-100 transition-opacity font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded text-xs"
                        style={{ top: itemPadding }}
                      >
                        #{index + 1}
                      </div>
                      <TextClamp maxLines={maxLines} lineHeight={lineHeight}>
                        {text}
                      </TextClamp>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm italic">
                {labels.noResponses}
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
